<template lang="pug">
v-container
  div.text-lg.font-medium.basic700--text {{ $t('donation_amounts') }}
  div.text-sm.font-normal.basic600--text {{ $t('donation_amounts_desc') }}

  v-form(ref="form")
    v-simple-table.my-6(style="border-bottom: none")
      thead
        tr
          th.text-md.font-medium.basic700--text.th-style(style="width: 150px;") {{ $t('amount') }}*
          th.text-md.font-medium.basic700--text.th-style {{ $t('description') }}
          th.text-md.font-medium.basic700--text.th-style.text-center {{ $t('allow_multiple_selection') }}?
          th.text-md.font-medium.basic700--text.th-style(style="width: 25%;")
      tbody
        template(v-for="(product, index) in products")
          tr
            td.py-1.th-style
              v-text-field.text-md.font-normal.round-xl(v-model="product.name" :prefix="$currency.symbol" type="number" min="5" :placeholder="(25*(index+1)).toString()" outlined hide-details :rules="rules.has_price")
            td.py-1.th-style
              v-text-field.text-md.font-normal.round-xl(v-model="product.description" :placeholder="$t('description_optional')" outlined hide-details)
            td.py-1.th-style
              div.d-flex.justify-center
                div
                  y-switch.mb-n3.pa-0(v-model="product.allow_multiple")
            td.py-1.th-style
              v-btn(v-if="products.length > 1" icon @click="clickDeleteProduct(index)")
                v-icon(color="danger500" size="30") $yzi-trash-04

    div.pl-4
      template(v-if="products.length < 7")
        v-btn.round-lg.heading-6.px-4(color="info500" dark depressed height="48" @click="clickAddProduct")
          v-icon(left) mdi-plus
          | {{ $t('add_donation_amount')}}
      template(v-else)
        v-card.px-4.py-2.text-md.font-medium(flat tile color="warning100") {{ $t('donation_product_limit_reached') }}

    div.px-4.mt-6
      yc-checkbox.my-2(v-model="customAmount")
        template(v-slot:label)
          div.text-md.font-normal.basic700--text.ml-4 {{ $t('show_an_other_option') }}
      yc-checkbox(v-model="requireMinAmount")
        template.my-2(v-slot:label)
          div.text-md.font-normal.basic700--text.ml-4 {{ $t('require_min_donation_amount') }}

      template(v-if="requireMinAmount")
        v-row(no-gutters)
          v-col(cols="12" sm="5")
            v-card.round-lg.pa-4.ml-8.mt-3(flat color="basic300")
              div.heading-7.basic700--text {{ $t('set_minimum_amount') }}
              v-text-field.text-md.font-normal.round-xl.mt-2(v-model="minAmount" background-color="white" :prefix="$currency.symbol" :placeholder="$t('set_minimum_amount')" type="number" outlined hide-details="auto" :rules="rules.has_price")

  div.d-flex.mt-5
    v-spacer
    v-btn.round-lg.heading-6.px-8(color="info500" dark depressed height="48" :dark="!saveDisabled" :disabled="saveDisabled" :loading="isLoading" @click="saveData") {{ $t('save') }}
  
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'DetailsDonationAmounts',
  data () {
    return {
      isLoading: false,
      products: [],
      customAmount: false,
      customAmountId: null,
      requireMinAmount: false,
      minAmount: null,
      rules: {
        not_empty: [val => (val || '').length > 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 5|| `Amount should be more than ${this.$currency.symbol} 5`],
      },
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      campaign: 'donation/getCampaign'
    }),
    saveDisabled () {
      if (this.products.length > 0) {
        return false
      } else if (this.customAmount) {
        return false
      }
      return true
    },
  },
  mounted () {
    this.setData(this.campaign)
  },
  methods: {
    ...mapActions(['showMessage']),
    setData (campaign) {
      if (campaign.form.min_amount) {
        this.requireMinAmount = true
        this.minAmount = campaign.form.min_amount
      }
      if (campaign.form.products.length > 0) {
        this.products = []
        campaign.form.products.forEach(o => { 
          if (o.imaginary) {
            this.customAmount = true
            this.customAmountId = o.id
          } else {
            this.products.push({ id: o.id, name: o.price / this.$currency.multiplier, description: o.description, price: o.price, allow_multiple: !o.order_limit_enabled })
          }
        })
      } else {
        this.products.push({name: null, description: null, price: null, order_limit_enabled: true, allow_multiple: false })
      }
    },
    clickAddProduct () {
      this.products.push({name: null, description: null, price: null, order_limit_enabled: true, allow_multiple: false })
    },
    clickDeleteProduct (idx) {
      this.products.splice(idx, 1)
    },
    saveData () {
      if (!this.$refs.form.validate()) {
        return
      }
      this.isLoading = true
      let dataProducts = []
      this.products.forEach(o => {
        let dataProduct = {
          id: o.id,
          name: `${this.$currency.symbol} ${o.name}`,
          description: o.description,
          price: Number(o.name) * this.$currency.multiplier,
          order_limit_enabled: !o.allow_multiple,
        }
        dataProducts.push(dataProduct)
      })
      if (this.customAmount) {
        dataProducts.push({ id: this.customAmountId, imaginary: true })
      }
      let data = {
        products: dataProducts,
        form: {
          min_amount: this.requireMinAmount ? this.minAmount : null
        }
      }
      this.$axios.patch(`/donations/${this.company.slug}/campaigns/${this.campaign.form.slug}`, data)
        .then(resp => {
          this.$store.commit('donation/setCampaign', resp.data)
          this.setData(resp.data)
          this.showMessage(['success', this.$t('success_message.successfully_updated')])
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  },
}
</script>

<style scoped>
.th-style {
  border-bottom: none !important
}
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-basic500);
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
</style>